import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import {
  TokenStateMap,
  AFMessage,
  TokenState,
  AFMessageStateData,
  FeTokenAlertStateData,
  AlertType,
} from './types';
import api, { GetSnapshotResponse, GetTokensResponse } from '../../api';
import { useAFStore } from './afStore';
import { DataAnalyzer } from './dataAnalyzer';
import { FilterFormData } from '../ui/uiStore';

interface WebSocketState {
  isConnected: boolean;
  lastConnected?: number;
  reconnectAttempts: number;
  error?: string;
  lastMessageTime?: number;
  healthCheckInterval?: number;
  isHealthCheckActive?: boolean;
}

// 로깅 유틸리티 함수들 (개발 환경에서만 로그 출력)
const isDevelopment = process.env.NODE_ENV === 'development';

const logger = {
  // 초기화 관련 로그
  initialization: {
    start: () => isDevelopment && console.log('🚀 데이터 초기화 시작'),
    status: (isInitializing: boolean, isInitialized: boolean) =>
      isDevelopment && console.log('🔄 초기화 상태 확인:', { isInitializing, isInitialized }),
    afDataRequest: () => isDevelopment && console.log('📡 AF 데이터 요청 중...'),
    afDataComplete: (count: number) => isDevelopment && console.log(`✅ AF 데이터 로드 완료: ${count}개`),
    afDataError: (error: any) => console.error('❌ AF 데이터 로드 실패:', error), // 에러는 항상 출력
    snapshotRequest: () => isDevelopment && console.log('📡 스냅샷 데이터 요청 중...'),
    snapshotComplete: () => isDevelopment && console.log('✅ 스냅샷 데이터 로드 완료'),
    snapshotError: (error: any) => console.error('❌ 스냅샷 데이터 로드 실패:', error), // 에러는 항상 출력
    appMount: () => isDevelopment && console.log('🚀 App 컴포넌트 마운트 - 데이터 초기화 시작')
  },

  // WebSocket 관련 로그
  websocket: {
    messageReceived: (type: string) => isDevelopment && console.log('📨 WebSocket 메시지 수신:', type),
    unknownMessageType: (type: string) => console.warn('알 수 없는 메시지 타입:', type), // 경고는 항상 출력
    unknownDataType: (type: string) => console.warn('알 수 없는 데이터 타입:', type) // 경고는 항상 출력
  },
};

export interface DataStoreState {
  // state
  tokenStateMap: TokenStateMap;
  isInitialized: boolean;
  isInitializing: boolean;
  dataAnalyzer: DataAnalyzer;
  error: string | null;

  //actions
  setConnectionStatus: (status: Partial<WebSocketState>) => void;

  // 초기화 및 큐 처리
  initializeData: () => Promise<void>;
  // WebSocket 메시지 처리
  handleTokenUpdateMessage: (message: AFMessage) => void;

  initializeTokenStateMap: (response: GetTokensResponse | GetSnapshotResponse) => TokenStateMap;
  updateTokenStateMap: (tokenAddress: string, stateKey: keyof TokenState, newState: AFMessageStateData | boolean | number | Partial<Record<AlertType, FeTokenAlertStateData>>) => void;
  getTokenData: (tokenAddress: string) => TokenState | undefined;
  getFilteredTokenData: (filters: FilterFormData | null) => TokenState[];
  fetchAndAddTokenState: (tokenAddress: string) => Promise<void>;
}

export const useDataStore = create<DataStoreState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // 기본 상태
      isConnected: false,
      reconnectAttempts: 0,
      lastMessageTime: undefined,
      healthCheckInterval: 60000, // 1분
      isHealthCheckActive: false,
      tokenStateMap: {},
      // 초기화 상태
      isInitialized: false,
      isInitializing: false,
      error: null,

      // 데이터 큐
      dataQueue: [],

      // 연결 상태 설정
      setConnectionStatus: (status) => {
        set((state) => ({ ...state, ...status }));
      },

      // 초기화 및 큐 처리
      initializeData: async () => {
        const state = get();

        // 이미 초기화 중이거나 완료된 경우 중복 실행 방지
        if (state.isInitializing || state.isInitialized) {
          logger.initialization.status(state.isInitializing, state.isInitialized);
          return;
        }

        logger.initialization.start();
        set({ isInitializing: true });

        try {
          // 1. AF 데이터 로드
          logger.initialization.afDataRequest();
          try {
            const afResponse = await api.getAFsByGroupId();
            useAFStore.getState().setAFData(afResponse);
            logger.initialization.afDataComplete(afResponse.length);
          } catch (afError) {
            logger.initialization.afDataError(afError);
            // AF 데이터 로드 실패는 전체 초기화를 중단하지 않음
          }

          // 2. REST API로 스트림 정보를 가져오고, /snapshot 호출
          logger.initialization.snapshotRequest();
          const getSnapshotResponse = await api.getSnapshot();
          const tokenStateMap = get().initializeTokenStateMap(getSnapshotResponse);

          // 2. tokenStateMap에 스냅샷 데이터 저장
          set((state) => {
            const newTokenStateMap = {
              ...state.tokenStateMap,
              ...tokenStateMap
            };

            return {
              tokenStateMap: newTokenStateMap,
              isInitialized: true,
              isInitializing: false
            };
          });

          logger.initialization.snapshotComplete();
          get().dataAnalyzer.checkQueue();
        } catch (error) {
          logger.initialization.snapshotError(error);
          set({
            isInitializing: false,
            error: '스냅샷 데이터 로드 실패'
          });
        }
      },

      handleTokenUpdateMessage: (message: AFMessage) => {
        logger.websocket.messageReceived(message.type);
        
        // 마지막 메시지 시간 업데이트
        get().setConnectionStatus({ lastMessageTime: Date.now() });
        
        if (get().dataAnalyzer.isAFMessageType(message)) {
          get().dataAnalyzer.addToQueue([message]);
        } else {
          logger.websocket.unknownMessageType(message.type);
        }
      },

      addNewTokenState: (tokenAddress: string, tokenState: TokenState) => {
        set((state) => ({
          tokenStateMap: { ...state.tokenStateMap, [tokenAddress]: tokenState }
        }));
      },

      initializeTokenStateMap: (response: GetTokensResponse | GetSnapshotResponse): TokenStateMap => {
        const tokenStateMap: { [tokenAddress: string]: TokenState } = {};
        Object.entries(response).forEach(([tokenAddress, items]) => {
          tokenStateMap[tokenAddress] = {
            ...get().dataAnalyzer.createTokenState(tokenAddress, items),
            tokenAddress: tokenAddress,
          };
        });
        return tokenStateMap;
      },

      updateTokenStateMap: (tokenAddress: string, stateKey: keyof TokenState, newState: AFMessageStateData | boolean | number | Partial<Record<AlertType, FeTokenAlertStateData>>) => {
        set((state) => ({
          tokenStateMap: { ...state.tokenStateMap, [tokenAddress]: { ...state.tokenStateMap[tokenAddress], [stateKey]: newState } }
        }));
      },

      getTokenData: (tokenAddress: string) => {
        return get().tokenStateMap[tokenAddress];
      },

      getFilteredTokenData: (filters: FilterFormData | null) => {
        if (!filters) {
          return Object.values(get().tokenStateMap);
        }

        return Object.values(get().tokenStateMap).filter(token => {
          const { statistics, ranking } = token;
          if (!statistics) return false;

          // 각 필터 조건 검사
          if (filters.currentAfCnt.min !== undefined && statistics.currentAfCnt < filters.currentAfCnt.min) return false;
          if (filters.currentAfCnt.max !== undefined && statistics.currentAfCnt > filters.currentAfCnt.max) return false;
          
          if (filters.athAfCnt.min !== undefined && statistics.athAfCnt < filters.athAfCnt.min) return false;
          if (filters.athAfCnt.max !== undefined && statistics.athAfCnt > filters.athAfCnt.max) return false;
          
          if (filters.atlMc.min !== undefined && statistics.atlMc < filters.atlMc.min) return false;
          if (filters.atlMc.max !== undefined && statistics.atlMc > filters.atlMc.max) return false;
          
          if (filters.athMc.min !== undefined && statistics.athMc < filters.athMc.min) return false;
          if (filters.athMc.max !== undefined && statistics.athMc > filters.athMc.max) return false;
          
          if (filters.mc.min !== undefined && statistics.lastFlow.mc < filters.mc.min) return false;
          if (filters.mc.max !== undefined && statistics.lastFlow.mc > filters.mc.max) return false;
          
          if (filters.currentTokenAmountInDecimals.min !== undefined && statistics.currentTokenAmountInDecimals < filters.currentTokenAmountInDecimals.min) return false;
          if (filters.currentTokenAmountInDecimals.max !== undefined && statistics.currentTokenAmountInDecimals > filters.currentTokenAmountInDecimals.max) return false;
          
          if (filters.athTokenAmountInDecimals.min !== undefined && statistics.athTokenAmountInDecimals < filters.athTokenAmountInDecimals.min) return false;
          if (filters.athTokenAmountInDecimals.max !== undefined && statistics.athTokenAmountInDecimals > filters.athTokenAmountInDecimals.max) return false;
          
          if (filters.totalUsedUsd.min !== undefined && statistics.totalUsedUsd < filters.totalUsedUsd.min) return false;
          if (filters.totalUsedUsd.max !== undefined && statistics.totalUsedUsd > filters.totalUsedUsd.max) return false;
          
          if (filters.cumulativeConvictionAtSameTimeRank.min !== undefined && ranking.cumulativeConvictionAtSameTimeRank < filters.cumulativeConvictionAtSameTimeRank.min) return false;
          if (filters.cumulativeConvictionAtSameTimeRank.max !== undefined && ranking.cumulativeConvictionAtSameTimeRank > filters.cumulativeConvictionAtSameTimeRank.max) return false;

          return true;
        });
      },

      fetchAndAddTokenState: async (tokenAddress: string) => {
        try {
          const response = await api.getStreamsByAddresses([tokenAddress]);
          const newTokenMap = get().initializeTokenStateMap(response);
          
          if (newTokenMap[tokenAddress]) {
            set((state) => ({
              tokenStateMap: {
                ...state.tokenStateMap,
                [tokenAddress]: newTokenMap[tokenAddress]
              }
            }));
          }
        } catch (error) {
          console.error(`❌ fetchAndAddTokenState 에러 (${tokenAddress}):`, error);
        }
      },

      // DataAnalyzer 관련
      dataAnalyzer: DataAnalyzer.create(
        () => get(), // getStoreState
      ),


    })),
    {
      name: 'WebSocketStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
); 